import { Component, OnInit, AfterViewInit, Inject } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Location } from '@angular/common';
import { DomSanitizer, Meta, SafeHtml, Title } from '@angular/platform-browser';

import { take } from 'rxjs';

import { Advert } from '../../models/advert.model';
import { Candidate } from '../../models/candidate.model';
import { Applicant } from '../../models/applicant.model';
import { JobPosting } from '../../models/job-posting.model';
import { AdvertService } from '../../services/advert.service';
import { AccountService } from '../../services/account.service';
import { StringOrNumber } from '../../datatypes/misc';
import { PreviousRouteService } from '../../services/previous-route.service';
import { HelpersService } from 'src/app/helpers/helpers.service';
import { CorporateConfig } from 'src/app/corporate.config';
import { AuthenticationService } from '../../services/authentication.service';
import { AppConfig } from 'src/app/app.config';
import { DOCUMENT } from '@angular/common';

@Component({
    selector: 'jobdetails',
    templateUrl: './jobdetails.component.html',
    styleUrls: ['./jobdetails.component.css']
})
export class JobDetailsComponent implements OnInit, AfterViewInit {

    model: Advert;
    previousApplication: Applicant;
    isAdvertPreview: boolean = false;
    private candidate: Candidate;

    // jobId could be the advert PK or a guid
    public jobId = this.route.snapshot.params['id'];

    isUserLoggedIn = false;

    public isComponentLoading = true;
    public isAdvertOpen = false;
    public isAdvertPublished = false;
    public viewGoBackLink = false;

    public jsonLD: SafeHtml;
    public showDownloadApplicationFormButton: boolean;

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private advertService: AdvertService,
        private accountService: AccountService,
        private authenticationService: AuthenticationService,
        private sanitizer: DomSanitizer,
        private location: Location,
        private previousRouteService: PreviousRouteService,
        private metaService: Meta,
        private titleService: Title,
        private helperService: HelpersService,
        @Inject(DOCUMENT) private document: Document
    ) {
    }

    ngOnInit() {
        
        this.authenticationService.isUserLoggedin.pipe(
            take(1),
          ).subscribe({
          next: (res) => this.isUserLoggedIn = res,
          error: () => this.isUserLoggedIn = false,
          complete: () => {
            if (!this.router.url.includes('job') && this.router.url.includes('advert-preview')) {
                this.getAdvertPreviewData();
                this.isAdvertPreview = true;
            } else {
                this.getJobData();
            }
          }
        });
    }
   
    ngAfterViewInit() {
        this.checkPrevLink();
    }

    noPreviousApplications(): boolean {
        if (!this.isUserLoggedIn) {
            return true;
        }
        return (this.candidate != null || this.candidate != undefined) && !this.previousApplications();
    }

    previousApplications(): boolean {
        return this.previousApplication != null;
    }

    navigateToApply() {
        this.router.navigate(['./apply', this.model.JobTitle, this.model.AdvertId])
    }

    navigateBack() {
        this.location.back();
    }

    downloadApplicationForm() {
        if (this.isAdvertPreview) {
            return;
        }
        if (!!this.model.ClientApplicationFormGuid) {
            this.accountService.downloadDocumentByGuid(this.model.ClientApplicationFormGuid, 'DownloadClientApplicationForm');
        }
    }

    downloadFurtherInformation() {
        if (this.isAdvertPreview) {
            return;
        }

        if (!!this.model.JobDescriptionGuid) {
            this.accountService.downloadDocumentByGuid(this.model.JobDescriptionGuid, 'FurtherInformationDownload');
        }
    }

    private getAdvertPreviewData() {
        const guid = this.route.snapshot.params['guid'];

        this.advertService.getAdvertPreview(guid)
            .subscribe(result => {
                this.initialiseModel(result);
            });
    }


    private getJobData() {
        // check if the advert is an integer or a guid ...
        const isJobIdNumber = this.isNumber(this.jobId);

        if (isJobIdNumber) {
            this.advertService.getAdvert(+this.jobId)
                .subscribe(result => {
                    this.initialiseModel(result);
                });
        } else {
            const guid = `${this.jobId}`;
            this.advertService.getAdvertByGuid(guid)
                .subscribe(result => {
                    this.initialiseModel(result);
                });
        }
        if (this.isUserLoggedIn) {
            this.accountService.getCandidate().subscribe(res => {
                this.setCandidate(res);
                this.noPreviousApplications()
            });

            if (isJobIdNumber) {
                this.accountService.alreadyApplied(+this.jobId).subscribe(res => this.setPreviousApplication(res));
            } else {
                const guid = `${this.jobId}`;
                this.accountService.alreadyAppliedGuid(guid).subscribe(res => this.setPreviousApplication(res));
            }
        }
    }

    private isNumber(value: StringOrNumber): boolean {
        return !isNaN(Number(value.toString()));
    }

    private initialiseModel(result: Advert) {
        this.setModel(result);
        this.isComponentLoading = false;
    }

    private setModel(result: Advert) {
        this.model = result;
        this.showDownloadApplicationFormButton = !!this.model.ClientApplicationFormName
        && this.model.ApplicationTypeName === "Application Form, CV & Cover Letter";

        if (this.model === null) {
            this.router.navigate(['/home']);
        } else {
            this.checkAdvertStatus();
            const jobPostingLD = new JobPosting(
                this.model.JobTitle,
                this.model.AdvertText,
                this.model.ContactInformation,
                this.model.PublishDate,
                this.model.ClosingDate,
                this.model.HoursName ? this.model.HoursName.toUpperCase().replace(' ', '_') : '',
                this.model.JobCategories.length > 0 ? this.model.JobCategories.map(x => x.Name) : null,
                'GBP',
                this.calculateSalary(this.model.SalaryFrom, this.model.SalaryTo, this.model.SalaryComment),
                this.model.Address ? this.model.Address.County : null,
                this.model.Address ? this.model.Address.Locality : null,
                this.model.Address ? this.model.Address.PostCode : null,
                this.model.Address ? this.getStreetAddress(this.model.Address.Number, this.model.Address.Street) : null,
                this.model.Address ? this.model.Address.Country : null);

            this.jsonLD = this.getSafeHTML(jobPostingLD);
        }

        this.setPageTitleAndMetaDescription();
    }

    private setPageTitleAndMetaDescription() {
        const title = this.helperService.getPageContent(CorporateConfig.advertTitle)?.replace('<jobTitle>', this.model.JobTitle);
        this.titleService.setTitle(title);

        const strippedAdvertText = this.model.AdvertText?.replace(/(<([^>]+)>)/gi, "").trim();

        const advertText = strippedAdvertText.length > 150 ? `${strippedAdvertText.substring(0, 149)}...` : strippedAdvertText;

        this.metaService.updateTag({name: 'description', content: `${advertText}`});
    }

    private setCandidate(result: Candidate) {
        this.candidate = result;
    }

    private setPreviousApplication(applicant: Applicant) {
        this.previousApplication = applicant;
    }

    private checkAdvertStatus() {
        if (this.model.DraftStatus) {
            this.isAdvertPublished = false;
            return;
        } else {
            this.isAdvertPublished = true;
        }

        const publishDate = new Date(this.model.PublishDate);
        const closingDate = new Date(this.model.ClosingDate);

        this.isAdvertPublished = publishDate.getTime() < Date.now();

        this.isAdvertOpen = closingDate.getTime() > Date.now();
    }

    private getSafeHTML(value: {}) {
        const json = value
            ? JSON.stringify(value, null, 2)?.replace(/<\/script>/g, '<\\/script>')
            : '';
        const html = `<script type="application/ld+json">${json}</script>`;
        return this.sanitizer.bypassSecurityTrustHtml(html);
    }

    private calculateSalary(salaryFromValue: number, salaryToValue: number, salaryComment: string): string {
        if (salaryFromValue !== 0 && salaryToValue !== 0 && salaryFromValue !== salaryToValue) {
            if (salaryComment !== '') {
                return '£' + salaryFromValue + ' - ' + '£' + salaryToValue + ' ' + salaryComment;
            }
            return '£' + salaryFromValue + ' - ' + '£' + salaryToValue;
        }

        if (salaryFromValue !== 0 && salaryFromValue === salaryToValue) {
            if (salaryComment !== '') {
                return '£' + salaryFromValue + ' ' + salaryComment;
            }
            return '£' + salaryFromValue;
        }

        if (salaryFromValue !== 0 && salaryToValue === 0) {
            if (salaryComment !== '') {
                return 'From ' + '£' + salaryFromValue + ' ' + salaryComment;
            }
            return 'From ' + '£' + salaryFromValue;
        }

        if (salaryToValue !== 0 && salaryFromValue === 0) {
            if (salaryComment !== '') {
                return 'Up to ' + '£' + salaryToValue + ' ' + salaryComment;
            }
            return 'Up to ' + '£' + salaryToValue;
        }

        if (salaryToValue === 0 && salaryFromValue === 0 && salaryComment !== '') {
            return salaryComment;
        }

        return '';
    }

    private getStreetAddress(number: string, street: string): string {
        if (number !== null && street !== null) {
            return number + ', ' + street;
        }

        if (number == null && street !== null) {
            return street;
        }

        return '';
    }

    private checkPrevLink(): boolean {
        return this.viewGoBackLink = (this.previousRouteService.getPreviousUrl() === '/search');
    }    
}
